import { useKitData, useKitDeletionPreview } from "@/data/use-kits-data";
import { GetKitDeletionPreviewResponseDto, GetKitDto } from "@/lib/gen-api";
import { kitLabel } from "@/lib/kit-label";
import { cn } from "@/lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPowerOff,
  faTrash,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ExpertIcon,
  FolderIcon,
  SkillIcon,
  TemplateIcon,
} from "../display/display-icon/artifacts";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { Column, Row } from "../ui/flex";

type KitDeletionButtonProps = {
  groupUuid: string;
  kitUuid: string;
  children?: React.ReactElement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onDelete?: () => void;
};

export const KitDeletionDialog = ({
  open: openProp,
  onOpenChange,
  groupUuid,
  kitUuid,
  children,
  onDelete,
}: KitDeletionButtonProps) => {
  const [open, setOpen] = useState(false);

  const isOpened = openProp ?? open;
  const setIsOpened = onOpenChange ?? setOpen;

  return (
    <AlertDialog open={isOpened} onOpenChange={setIsOpened}>
      {children && <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>}
      {isOpened && (
        <Content
          groupUuid={groupUuid}
          kitUuid={kitUuid}
          onDelete={onDelete}
          onClose={() => setIsOpened(false)}
        />
      )}
    </AlertDialog>
  );
};

export const useDeactivationAndDeletionCounts = (
  deletionPreview: GetKitDeletionPreviewResponseDto | undefined,
) => {
  return useMemo(() => {
    let countDeletions = 0;
    let countDeactivations = 0;

    if (!deletionPreview) {
      return { countDeletions, countDeactivations };
    }

    const artifactLists = [
      deletionPreview.experts,
      deletionPreview.templates,
      deletionPreview.folders,
      deletionPreview.skills,
      deletionPreview.scripts,
    ];

    for (const artifacts of artifactLists) {
      for (const artifact of artifacts) {
        switch (artifact.action) {
          case "delete":
            countDeletions++;
            break;
          case "deactivate":
            countDeactivations++;
            break;
        }
      }
    }

    return { countDeletions, countDeactivations };
  }, [deletionPreview]);
};

function Content({
  groupUuid,
  kitUuid,
  onDelete,
  onClose,
}: {
  groupUuid: string;
  kitUuid: string;
  onDelete?: () => void;
  onClose: () => void;
}) {
  const { deletionPreview, isLoading } = useKitDeletionPreview(
    groupUuid,
    kitUuid,
  );

  const { kit, deleteKit, isDeletingKit } = useKitData(groupUuid, kitUuid);

  const { countDeletions, countDeactivations } =
    useDeactivationAndDeletionCounts(deletionPreview);

  const handleDelete = async () => {
    if (!kit) return;
    try {
      await deleteKit(kitUuid);
    } catch {
      toast.error(`Failed to delete ${kitLabel(kit).toLowerCase()}.`);
      return;
    }
    toast.success(`${kitLabel(kit)} ${kit.name} was deleted.`);
    onClose();
    onDelete?.();
  };

  if (!kit) {
    return null;
  }

  const label = kitLabel(kit);

  return (
    <AlertDialogContent className="sm:max-w-[800px]">
      <AlertDialogHeader>
        <AlertDialogTitle>
          Delete {label}: {kit.name}?
        </AlertDialogTitle>
      </AlertDialogHeader>
      <AlertDialogDescription className="relative flex h-full flex-col gap-2 overflow-y-auto text-sm">
        <KitDeletionPreviewContent
          kit={kit}
          deletionPreview={deletionPreview}
        />
      </AlertDialogDescription>
      <AlertDialogFooter>
        <AlertDialogCancel disabled={isDeletingKit}>Cancel</AlertDialogCancel>
        <Button
          variant="destructive"
          size="sm"
          onClick={handleDelete}
          disabled={isLoading || isDeletingKit}
        >
          Delete Kit{" "}
          {countDeletions + countDeactivations > 0 && (
            <>
              &{" "}
              {countDeletions > 0 && countDeactivations > 0
                ? "Delete and Deactivate"
                : countDeletions > 0
                  ? "Delete"
                  : "Deactivate"}{" "}
              {countDeletions + countDeactivations > 1
                ? "Artifacts"
                : "Artifact"}
            </>
          )}
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  );
}

type KitDeletionPreviewContentProps = {
  kit: GetKitDto;
  deletionPreview: GetKitDeletionPreviewResponseDto | undefined;
};

export const KitDeletionPreviewContent: React.FC<
  KitDeletionPreviewContentProps
> = ({ kit, deletionPreview }) => {
  const label = kitLabel(kit);
  const { countDeletions, countDeactivations } =
    useDeactivationAndDeletionCounts(deletionPreview);

  if (!deletionPreview) {
    return null;
  }

  return (
    <Column className="gap-4">
      <div>This {label} will be deleted permanently.</div>
      {deletionPreview.linkedProjects.length > 0 && (
        <div className="rounded border border-red-600 p-3 text-sm font-bold text-red-600">
          This {label} is linked into {deletionPreview.linkedProjects.length}{" "}
          {deletionPreview.linkedProjects.length === 1
            ? "Workbook"
            : "Workbooks"}{" "}
          ({deletionPreview.linkedProjects.map((p) => p.name).join(", ")}).
          Deleting it will remove those links.
        </div>
      )}
      {deletionPreview.sharedGroups.length > 0 && (
        <div className="rounded border border-red-600 p-3 text-sm font-bold text-red-600">
          This {label} is shared with {deletionPreview.sharedGroups.length}{" "}
          other {deletionPreview.sharedGroups.length === 1 ? "group" : "groups"}{" "}
          ({deletionPreview.sharedGroups.map((w) => w.name).join(", ")}
          ). Deleting it will revoke their access.
        </div>
      )}
      {countDeletions === 0 && countDeactivations === 0 && (
        <div>This {label} has no shared artifacts to delete or deactivate.</div>
      )}
      {countDeletions > 0 && countDeactivations === 0 && (
        <div>
          {countDeletions} shared{" "}
          {countDeletions === 1 ? "artifact" : "artifacts"} will be deleted
          permanently.
        </div>
      )}
      {countDeactivations > 0 && countDeletions === 0 && (
        <div>
          {countDeactivations} shared{" "}
          {countDeactivations === 1 ? "artifact" : "artifacts"} will be
          deactivated.
        </div>
      )}
      {countDeletions > 0 && countDeactivations > 0 && (
        <div>
          {countDeletions} shared{" "}
          {countDeletions === 1 ? "artifact" : "artifacts"} will be deleted
          permanently and {countDeactivations} shared{" "}
          {countDeactivations === 1 ? "artifact" : "artifacts"} will be
          deactivated.
        </div>
      )}
      {countDeletions > 0 && (
        <div>
          Deletions can not be undone. Deletions can only happen when there are
          no artifacts that directly depend on the artifact to be deleted.
        </div>
      )}
      {countDeactivations > 0 && (
        <div>
          Deactivations are caused when the artifact is a direct dependencies of
          other artifacts. A deactivated artifact will still exist, but will not
          be listed and will not be available for creation of new artifacts.
        </div>
      )}

      {deletionPreview.experts.length > 0 && (
        <Column className="gap-4">
          <Row className="items-center gap-4 font-bold">
            <ExpertIcon /> Shared Experts
          </Row>
          {deletionPreview.experts.map((expert) => (
            <ArtifactDeletion key={expert.uuid} artifact={expert} />
          ))}
        </Column>
      )}
      {deletionPreview.templates.length > 0 && (
        <Column className="gap-4">
          <Row className="items-center gap-4 font-bold">
            <TemplateIcon /> Shared Templates
          </Row>
          {deletionPreview.templates.map((template) => (
            <ArtifactDeletion key={template.uuid} artifact={template} />
          ))}
        </Column>
      )}
      {deletionPreview.folders.length > 0 && (
        <Column className="gap-4">
          <Row className="items-center gap-4 font-bold">
            <FolderIcon /> Shared Folders
          </Row>
          {deletionPreview.folders.map((folder) => (
            <ArtifactDeletion key={folder.uuid} artifact={folder} />
          ))}
        </Column>
      )}
      {deletionPreview.skills.length > 0 && (
        <Column className="gap-4">
          <Row className="items-center gap-4 font-bold">
            <SkillIcon /> Shared Skills
          </Row>
          {deletionPreview.skills.map((skill) => (
            <ArtifactDeletion key={skill.uuid} artifact={skill} />
          ))}
        </Column>
      )}
    </Column>
  );
};

const ArtifactDeletion = ({
  artifact,
}: {
  artifact: {
    name: string;
    groups: number;
    kits: number;
    dependents: number;
    action: "delete" | "deactivate";
  };
}) => {
  return (
    <Column
      className={cn("border-foreground-5 ml-2 gap-1 border-l-1 pl-6", {
        "border-red-600 text-red-600": artifact.action === "delete",
      })}
    >
      <Row className="font-xl items-center gap-2 font-bold">
        <div className="text-sm">{artifact.name}</div>
        <Row className="items-center gap-2">
          <span>(</span>
          <FontAwesomeIcon
            icon={artifact.action === "delete" ? faTrash : faPowerOff}
          />
          <span>{artifact.action === "delete" ? "Delete" : "Deactivate"}</span>
          <span>)</span>
        </Row>
      </Row>
      <Column className="gap-1">
        {artifact.groups === 0 &&
          artifact.kits === 0 &&
          artifact.dependents === 0 && (
            <div>Not used by any group, kit or dependent.</div>
          )}

        {artifact.groups > 0 && (
          <div className="text-xs text-gray-500">
            Remove {artifact.groups} Group Shares
          </div>
        )}

        {artifact.kits > 0 && (
          <div className="text-xs text-gray-500">
            Remove from {artifact.kits} Kits
          </div>
        )}

        {artifact.dependents > 0 && (
          <div className="text-xs text-gray-500">
            Keep {artifact.dependents} Dependents
          </div>
        )}
      </Column>
    </Column>
  );
};
